"use client"
import Link from "next/link"
import Image from "next/image"
import { Search, ShoppingCart, Menu, User, Heart, LogOut, X, Bell } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Badge } from "@/components/ui/badge"
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet"
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog"
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu"
import { ThemeToggle } from "@/components/theme-toggle"
import { useAuth } from "@/contexts/auth-context"
import { useCart } from "@/contexts/cart-context"
import { useWishlist } from "@/contexts/wishlist-context"
import { useState, useEffect } from "react"

import { CartDrawer } from "@/components/cart-drawer"
import { NotificationBell } from "@/components/notification-bell"
import { useToast } from "@/hooks/use-toast"
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { Skeleton } from "@/components/ui/skeleton"

export function Header() {
  const { user, isAuthenticated, isAdmin, logout } = useAuth()
  const { cart } = useCart()
  const { wishlist } = useWishlist()
  const { toast } = useToast()
  const [cartDrawerOpen, setCartDrawerOpen] = useState(false)
  const [searchModalOpen, setSearchModalOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState("")
  const [searchResults, setSearchResults] = useState([])
  const [isSearching, setIsSearching] = useState(false)
  const cartItemsCount = cart?.items?.reduce((sum, item) => sum + item.quantity, 0) || 0
  const wishlistCount = wishlist.length


  // Handle protected feature clicks
  const handleProtectedFeatureClick = (feature: string) => {
    if (!isAuthenticated) {
      toast({
        title: "Authentication Required",
        description: `Please sign in to access your ${feature}.`,
        variant: "default",
      })
    }
  }

  // Search functionality
  const handleSearch = async (query: string) => {
    if (query.length < 3) {
      setSearchResults([])
      return
    }

    setIsSearching(true)
    try {
      // Simulate API call - replace with actual search API
      const response = await fetch(`/api/search?q=${encodeURIComponent(query)}`)
      const data = await response.json()
      setSearchResults(data.results || [])
    } catch (error) {
      console.error('Search error:', error)
      setSearchResults([])
    } finally {
      setIsSearching(false)
    }
  }

  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      if (searchQuery.length >= 3) {
        handleSearch(searchQuery)
      } else {
        setSearchResults([])
      }
    }, 300)

    return () => clearTimeout(debounceTimer)
  }, [searchQuery])

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (searchQuery.trim()) {
      // Navigate to search results page
      window.location.href = `/search?q=${encodeURIComponent(searchQuery)}`
    }
  }

  return (
    <header className="sticky top-0 z-50 w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
      <div className="w-full max-w-[1920px] mx-auto px-4 sm:px-6 lg:px-8 xl:px-12 2xl:px-16 flex h-16 lg:h-20 xl:h-24 items-center justify-between">
        {/* Mobile: Hamburger + Logo */}
        <div className="flex items-center md:hidden">
          <Sheet>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" className="h-10 w-10 lg:h-12 lg:w-12" aria-label="Open menu">
                <Menu className="h-5 w-5 lg:h-6 lg:w-6" />
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="w-80 lg:w-96">
              <SheetTitle className="sr-only">Navigation Menu</SheetTitle>
              <nav className="flex flex-col space-y-4 lg:space-y-6">
                <Link href="/" className="flex items-center mb-2">
                  <Image
                    src="/aepac-logo-black-text.png"
                    alt="AEPAC"
                    width={120}
                    height={32}
                    className="h-8 w-auto"
                  />
                </Link>
                <Link href="/" className="ui-link hover:text-primary-blue transition-colors">Home</Link>
                <Link href="/shop" className="ui-link hover:text-primary-blue transition-colors">Shop</Link>
                <Link href="/about" className="ui-link hover:text-primary-blue transition-colors">About Us</Link>
                <Link href="/contact" className="ui-link hover:text-primary-blue transition-colors">Contact Us</Link>
                <div>
                  <div className="font-semibold text-sm text-muted-foreground mt-2 mb-1">Our Policies</div>
                  <div className="flex flex-col space-y-2 ml-4">
                    <Link href="/terms" className="ui-link hover:text-primary-blue transition-colors">Terms & Conditions</Link>
                    <Link href="/privacy" className="ui-link hover:text-primary-blue transition-colors">Privacy Policy</Link>
                    <Link href="/return-policy" className="ui-link hover:text-primary-blue transition-colors">Return Policy</Link>
                    <Link href="/shipping-policy" className="ui-link hover:text-primary-blue transition-colors">Shipping Policy</Link>
                  </div>
                </div>
              </nav>
            </SheetContent>
          </Sheet>
          <Link href="/" className="flex items-center ml-2">
            <Image
                    src="/aepac-logo-black-text.png"
                    alt="AEPAC"
                    width={120}
                    height={32}
                    className="h-8 w-auto"
                  />
          </Link>
        </div>
        {/* Desktop: Logo (unchanged) */}
        <div className="hidden md:flex items-center">
          <Link href="/" className="flex items-center">
            <Image
                    src="/aepac-logo-black-text.png"
                    alt="AEPAC"
                    width={120}
                    height={48}
                    className="h-8 lg:h-10 xl:h-12 w-auto"
                  />
          </Link>
        </div>

        {/* Center Section - Desktop Navigation */}
        <nav className="hidden md:flex items-center justify-center space-x-8 lg:space-x-10 xl:space-x-12 flex-1">
          <Link href="/" className="ui-link hover:text-primary-blue transition-colors">
            Home
          </Link>
          <Link href="/shop" className="ui-link hover:text-primary-blue transition-colors">
            Shop
          </Link>
          <Link href="/about" className="ui-link hover:text-primary-blue transition-colors">
            About Us
          </Link>
        </nav>

        {/* Right Section - Actions */}
        <div className="flex items-center space-x-1 lg:space-x-2 xl:space-x-3">
          <Button 
            variant="ghost" 
            size="icon" 
            className="h-10 w-10 lg:h-12 lg:w-12"
            aria-label="Search"
            onClick={() => setSearchModalOpen(true)}
          >
            <Search className="h-5 w-5 lg:h-6 lg:w-6" />
          </Button>
          <Button variant="ghost" size="icon" className="relative h-10 w-10 lg:h-12 lg:w-12" asChild>
            <Link href={isAuthenticated ? "/account?tab=wishlist" : "/auth/login"} onClick={() => handleProtectedFeatureClick("wishlist")}
                  aria-label="Wishlist">
              <Heart className="h-5 w-5 lg:h-6 lg:w-6" />
              {wishlistCount > 0 && (
                <Badge className="absolute -top-1 -right-1 h-5 w-5 lg:h-6 lg:w-6 rounded-full p-0 text-xs lg:text-sm flex items-center justify-center">
                  {wishlistCount > 99 ? '99+' : wishlistCount}
                </Badge>
              )}
            </Link>
          </Button>
          {isAuthenticated && <NotificationBell />}
          {isAuthenticated ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="h-10 w-10 lg:h-12 lg:w-12" asChild>
                  <Link href="/account" aria-label="Account">
                    <User className="h-5 w-5 lg:h-6 lg:w-6" />
                  </Link>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56 lg:w-64">
                <div className="px-2 py-1.5 lg:px-3 lg:py-2 text-sm lg:text-base">
                  <div className="font-medium">{user?.name || 'User'}</div>
                  <div className="text-xs lg:text-sm text-muted-foreground">{user?.email}</div>
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem asChild className="text-sm lg:text-base">
                  <Link href="/account">My Account</Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild className="text-sm lg:text-base">
                  <Link href="/account?tab=orders">My Orders</Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild className="text-sm lg:text-base">
                  <Link href="/account?tab=wishlist">Wishlist</Link>
                </DropdownMenuItem>
                {isAdmin && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem asChild className="text-sm lg:text-base">
                      <Link href="/admin">Admin Panel</Link>
                    </DropdownMenuItem>
                  </>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={logout} className="text-red-600 text-sm lg:text-base">
                  <LogOut className="mr-2 h-4 w-4 lg:h-5 lg:w-5" />
                  Sign Out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <Button variant="ghost" size="icon" className="h-10 w-10 lg:h-12 lg:w-12" asChild>
              <Link href="/auth/login" onClick={() => handleProtectedFeatureClick("account")}>
                <User className="h-5 w-5 lg:h-6 lg:w-6" />
              </Link>
            </Button>
          )}
          <Button 
            variant="ghost" 
            size="icon" 
            className="relative h-10 w-10 lg:h-12 lg:w-12" 
            aria-label="Cart"
            onClick={() => setCartDrawerOpen(true)}
          >
            <ShoppingCart className="h-5 w-5 lg:h-6 lg:w-6" />
            {cartItemsCount > 0 && (
              <Badge className="absolute -top-1 -right-1 h-5 w-5 lg:h-6 lg:w-6 rounded-full p-0 text-xs lg:text-sm flex items-center justify-center">
                {cartItemsCount > 99 ? '99+' : cartItemsCount}
              </Badge>
            )}
          </Button>
        </div>
      </div>

      {/* Mobile Search */}
      <div className="border-t px-4 lg:px-6 xl:px-8 py-2 lg:py-3 md:hidden">
        <div className="relative">
          <Search className="absolute left-3 lg:left-4 top-1/2 h-4 w-4 lg:h-5 lg:w-5 -translate-y-1/2 text-muted-foreground" />
          <Input 
            placeholder="Search Products & Categories" 
            className="pl-10 lg:pl-12 h-10 lg:h-12 text-sm lg:text-base" 
          />
        </div>
      </div>

      {/* Search Modal */}
      <Dialog open={searchModalOpen} onOpenChange={setSearchModalOpen}>
        <DialogContent className="sm:max-w-[600px] max-h-[80vh] overflow-hidden" aria-describedby="search-dialog-description">
          <DialogHeader>
            <DialogTitle>
              Search Products & Categories
            </DialogTitle>
            <DialogDescription id="search-dialog-description">
              Search for products and categories by entering keywords in the search box below.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <form onSubmit={handleSearchSubmit} className="relative">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Search Products & Categories..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 h-12 text-base"
                autoFocus
                suppressHydrationWarning
              />
            </form>

            {/* Search Results */}
            <div className="max-h-[400px] overflow-y-auto">
              {searchQuery.length > 0 && searchQuery.length < 3 && (
                <p className="text-sm text-muted-foreground text-center py-4">
                  Enter at least 3 characters to search
                </p>
              )}
              
              {isSearching && (
                <div className="space-y-4 py-6">
                  {[1, 2, 3].map((i) => (
                    <div key={i} className="flex items-center space-x-3 p-3 rounded-lg">
                      <Skeleton className="h-4 w-4 rounded" />
                      <div className="flex-1 space-y-2">
                        <Skeleton className="h-4 w-48" />
                        <Skeleton className="h-3 w-32" />
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {!isSearching && searchQuery.length >= 3 && searchResults.length === 0 && (
                <p className="text-sm text-muted-foreground text-center py-4">
                  No results found for "{searchQuery}"
                </p>
              )}

              {!isSearching && searchResults.length > 0 && (
                <div className="space-y-2">
                  {searchResults.map((result: any, index: number) => (
                    <Link
                      key={index}
                      href={result.url}
                      className="flex items-center space-x-3 p-3 rounded-lg hover:bg-muted transition-colors"
                      onClick={() => setSearchModalOpen(false)}
                    >
                      <div className="flex-1">
                        <h4 className="font-medium">{result.title}</h4>
                        <p className="text-sm text-muted-foreground">{result.description}</p>
                      </div>
                      <Search className="h-4 w-4 text-muted-foreground" />
                    </Link>
                  ))}
                </div>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Cart Drawer */}
      <CartDrawer open={cartDrawerOpen} onOpenChange={setCartDrawerOpen} />
    </header>
  )
}
